import { useEffect, useMemo, useState } from "react";
import { pitchLabel } from "@/lib/pitchLabel";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Clock, Download, Filter } from "lucide-react";
import { DatePickerUK } from "@/components/app/DatePickerUK";

const csvCell = (v) => {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function YearGroupBookings() {
  const { user } = useAuth();
  const leadAges = user?.year_group_lead_ages || [];

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [ageGroup, setAgeGroup] = useState("all");
  const [statusFilter, setStatusFilter] = useState("confirmed");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings/year-group-lead");
      setBookings(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cancel = async (b) => {
    if (!window.confirm(`Cancel ${b.team_name || "this booking"} on ${b.booking_date}?`)) return;
    try {
      await api.delete(`/bookings/${b.id}`);
      toast.success("Booking cancelled");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = useMemo(() => bookings.filter(b => {
    if (ageGroup !== "all" && b.age_group !== ageGroup) return false;
    if (statusFilter !== "all" && b.status !== statusFilter) return false;
    if (dateFrom && b.booking_date < dateFrom) return false;
    if (dateTo && b.booking_date > dateTo) return false;
    if (q) {
      const needle = q.toLowerCase();
      const hay = [b.pitch_name, b.team_name, b.opponent, b.notes, b.user_name].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  }), [bookings, ageGroup, statusFilter, dateFrom, dateTo, q]);

  const upcoming = filtered.filter(b => b.status === "confirmed").length;

  const exportCsv = () => {
    if (filtered.length === 0) return toast.error("Nothing to export");
    const headers = ["Date","Start","End","Age","Team","Session","Match type","Opponent","Pitch","Booked by","Status","Notes"];
    const rows = filtered.map(b => [
      b.booking_date,
      `${String(b.start_hour).padStart(2,"0")}:${String(b.start_min||0).padStart(2,"0")}`,
      `${String(b.end_hour).padStart(2,"0")}:${String(b.end_min||0).padStart(2,"0")}`,
      b.age_group || "", b.team_name || "",
      b.session_type || "training", b.match_type || "",
      b.opponent || "", b.pitch_name || "", b.user_name || "",
      b.status || "", (b.notes || "").replace(/\r?\n/g, " "),
    ]);
    const csv = [headers, ...rows].map(r => r.map(csvCell).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `year-group-bookings-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} bookings`);
  };

  if (leadAges.length === 0) {
    return (
      <div className="card-tactical rounded-md p-8 text-center opacity-70">
        <div className="font-display font-bold text-xl mb-2">Not a Year Group Lead yet</div>
        <p className="text-sm">Ask a committee admin to assign you as Year Group Lead for one or more age groups. Once set, every fixture in those year groups will show up here.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Year group lead</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2 leading-tight">Your year groups</h1>
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="ygl-badges">
            {leadAges.map(a => (
              <span key={a} className="text-[11px] tracking-widest uppercase px-2 py-0.5 rounded bg-[#83B68B]/10 border border-[#83B68B]/40 text-[#83B68B]">{a}</span>
            ))}
          </div>
        </div>
        <button
          onClick={exportCsv}
          data-testid="ygl-export-csv"
          className="btn-volt px-5 py-2.5 rounded-md inline-flex items-center gap-2 text-sm"
        >
          <Download className="w-4 h-4" /> Export ({filtered.length})
        </button>
      </div>

      <div className="card-tactical rounded-md p-5 mb-8" data-testid="ygl-filters">
        <div className="text-[10px] tracking-widest uppercase opacity-60 inline-flex items-center gap-2 mb-3">
          <Filter className="w-3 h-3" /> {upcoming} confirmed · {filtered.length} shown
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search…" className="bg-transparent border border-border rounded px-3 py-2 text-sm h-10" data-testid="ygl-search" />
          <select value={ageGroup} onChange={e=>setAgeGroup(e.target.value)} className="bg-transparent border border-border rounded px-3 py-2 text-sm h-10" data-testid="ygl-age">
            <option value="all">All my ages</option>
            {leadAges.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="bg-transparent border border-border rounded px-3 py-2 text-sm h-10" data-testid="ygl-status">
            <option value="all">All statuses</option>
            <option value="confirmed">Confirmed only</option>
            <option value="cancelled">Cancelled only</option>
          </select>
          <DatePickerUK value={dateFrom} onChange={setDateFrom} testId="ygl-from" placeholder="From" />
          <DatePickerUK value={dateTo} onChange={setDateTo} testId="ygl-to" placeholder="To" />
        </div>
      </div>

      {loading ? <div className="opacity-60 font-display tracking-widest">LOADING…</div> : filtered.length === 0 ? (
        <div className="card-tactical rounded-md p-8 text-center opacity-60">No bookings match these filters.</div>
      ) : (
        <div className="space-y-3" data-testid="ygl-list">
          {filtered.map(b => (
            <div key={b.id} className={`card-tactical rounded-md p-5 flex items-center justify-between gap-4 ${b.status === "cancelled" ? "opacity-50" : ""}`} data-testid={`ygl-row-${b.id}`}>
              <div className="flex items-center gap-5 flex-wrap">
                <div className="w-14 text-center">
                  <div className="font-display font-black text-3xl text-[#83B68B] leading-none">{format(parseISO(b.booking_date), "dd")}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase opacity-60 mt-1">{format(parseISO(b.booking_date), "MMM")}</div>
                </div>
                <div>
                  <div className="font-display font-bold text-xl">{b.team_name || "—"} <span className="opacity-60 text-sm">· {b.age_group}</span></div>
                  <div className="flex flex-wrap items-center gap-3 text-xs opacity-70 mt-1">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {String(b.start_hour).padStart(2,"0")}:{String(b.start_min || 0).padStart(2,"0")} — {String(b.end_hour).padStart(2,"0")}:{String(b.end_min || 0).padStart(2,"0")}</span>
                    <span className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded ${b.session_type === "game" ? "bg-[#83B68B]/20 text-[#83B68B]" : "bg-muted opacity-80"}`}>{b.session_type || "training"}{b.match_type ? ` · ${b.match_type}` : ""}</span>
                    <span>· {pitchLabel(b)}</span>
                    {b.opponent && <span>· vs {b.opponent}</span>}
                    <span className="opacity-60">· by {b.user_name}</span>
                    {b.status === "cancelled" && <span className="text-red-500">· Cancelled</span>}
                  </div>
                </div>
              </div>
              {b.status === "confirmed" && (
                <button onClick={()=>cancel(b)} data-testid={`ygl-cancel-${b.id}`} className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-border hover:border-red-400 hover:text-red-500 rounded transition-colors">
                  Cancel
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
